import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { formatDualCurrency } from '../utils/currency';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { Plus, Star, Eye, ChartLineUp, X as XIcon, GridFour, List as ListIcon } from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { ExpirationNudge } from '../components/ExpirationNudge';
import { getListingDurationDays } from '../utils/platformConfig';
import { useViewMode } from '../utils/useViewMode';
import { getCategoryName } from '../utils/categoryNames';
import { getStatusBucket, sortListings } from '../utils/myListingsHelpers';
import { MyListingsToolbar } from '../components/mylistings/MyListingsToolbar';
import { ListingCardGrid } from '../components/mylistings/ListingCardGrid';
import { ListingCardList } from '../components/mylistings/ListingCardList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMO_TIERS = [
  { key: 'featured', icon: Star, color: 'from-yellow-400 to-orange-500' },
  { key: 'top_category', icon: ChartLineUp, color: 'from-blue-400 to-indigo-500' },
  { key: 'homepage', icon: Eye, color: 'from-emerald-400 to-teal-500' },
];

export const MyListingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoListing, setPromoListing] = useState(null);
  const [promoStatuses, setPromoStatuses] = useState({});
  const [promoting, setPromoting] = useState(false);
  const [listingStats, setListingStats] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewMode, setViewMode] = useViewMode();

  const legacyFilter = searchParams.get('filter'); // back-compat deep links: active|expiring|expired|sold
  const [statusTab, setStatusTab] = useState(() =>
    ['active', 'expiring', 'expired', 'deleted'].includes(legacyFilter) ? legacyFilter : 'all'
  );
  const [soldOnly, setSoldOnly] = useState(legacyFilter === 'sold');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [search, setSearch] = useState('');

  const clearSoldFilter = () => {
    setSoldOnly(false);
    const next = new URLSearchParams(searchParams);
    next.delete('filter');
    setSearchParams(next);
  };

  useEffect(() => {
    if (user) fetchMyListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchMyListings = async () => {
    try {
      const { data } = await axios.get(`${API}/listings/user/${user.user_id}`, { withCredentials: true });
      setListings(data);
      const activeIds = data.filter((l) => l.status === 'active').map((l) => l.listing_id);
      const statsRes = await axios.get(`${API}/listings/user/${user.user_id}/stats`, { withCredentials: true }).catch(() => null);
      if (statsRes?.data) setListingStats(statsRes.data);
      const statuses = {};
      await Promise.all(activeIds.map(async (id) => {
        try {
          const { data: promos } = await axios.get(`${API}/listings/${id}/promotions`, { withCredentials: true });
          statuses[id] = promos;
        } catch { statuses[id] = []; }
      }));
      setPromoStatuses(statuses);
    } catch (error) {
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (tier) => {
    if (!promoListing) return;
    setPromoting(true);
    try {
      await axios.post(`${API}/listings/${promoListing.listing_id}/promote`, { tier }, { withCredentials: true });
      toast.success(t('promote.requestSent'));
      setPromoListing(null);
      fetchMyListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('promote.requestError'));
    } finally {
      setPromoting(false);
    }
  };

  const getPromoStatus = (listingId, tier) => {
    const promos = promoStatuses[listingId] || [];
    return promos.find((p) => p.tier === tier && (p.status === 'pending' || p.status === 'active'));
  };

  const handleRenew = async (listingId) => {
    try {
      const { data } = await axios.post(`${API}/listings/${listingId}/renew`, {}, { withCredentials: true });
      const days = data?.duration_days ?? await getListingDurationDays();
      toast.success(t('listing.renewed', { days }));
      fetchMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.renewFailed'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/listings/${deleteTarget.listing_id}`, { withCredentials: true });
      toast.success(t('listing.deleted'));
      setDeleteTarget(null);
      fetchMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.deleteFailed'));
    }
  };

  const handleRestore = async (listingId) => {
    try {
      await axios.post(`${API}/listings/${listingId}/restore`, {}, { withCredentials: true });
      toast.success(t('listing.restored'));
      fetchMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('listing.restoreFailed'));
    }
  };

  const counts = useMemo(() => {
    const c = { all: listings.length, active: 0, pending: 0, expiring: 0, expired: 0, deleted: 0, sold: 0 };
    listings.forEach((l) => {
      const bucket = getStatusBucket(l, t);
      if (c[bucket] !== undefined) c[bucket] += 1;
    });
    return c;
  }, [listings, t]);

  const categoryOptions = useMemo(() => {
    const seen = new Map();
    listings.forEach((l) => {
      if (l.category && !seen.has(l.category)) seen.set(l.category, getCategoryName(l.category, t));
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [listings, t]);

  const visibleListings = useMemo(() => {
    let list = listings;
    if (soldOnly) list = list.filter((l) => l.status === 'sold');
    if (statusTab !== 'all') list = list.filter((l) => getStatusBucket(l, t) === statusTab);
    if (category !== 'all') list = list.filter((l) => l.category === category);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((l) => l.title?.toLowerCase().includes(q));
    }
    return sortListings(list, sortBy);
  }, [listings, statusTab, category, search, sortBy, soldOnly, t]);

  const cardProps = { onDeleteClick: setDeleteTarget, onRestore: handleRestore, onRenew: handleRenew, onPromote: setPromoListing };

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-0">
      <Header />

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6 gap-3">
          <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide" data-testid="my-listings-title">
            {t('nav.myListings')}
          </h1>
          <div className="flex items-center gap-2">
            <div className="flex border border-border rounded-lg overflow-hidden" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white text-text_muted hover:text-text_main'}`}
                aria-label={t('myListings.gridView')}
                data-testid="view-mode-grid"
              >
                <GridFour size={18} weight={viewMode === 'grid' ? 'fill' : 'regular'} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 border-l border-border ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-text_muted hover:text-text_main'}`}
                aria-label={t('myListings.listView')}
                data-testid="view-mode-list"
              >
                <ListIcon size={18} weight={viewMode === 'list' ? 'fill' : 'regular'} />
              </button>
            </div>
            <Link to="/create">
              <Button className="bg-primary hover:bg-primary-hover text-white tracking-wide active:scale-95" data-testid="create-new-listing-button">
                <Plus size={20} className="mr-0 sm:mr-2" weight="bold" />
                <span className="hidden sm:inline">{t('nav.postAd')}</span>
              </Button>
            </Link>
          </div>
        </div>

        {!loading && statusTab === 'all' && !soldOnly && !search && category === 'all' && <ExpirationNudge variant="card" />}

        {soldOnly && (
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-warning-soft border border-warning text-xs font-medium text-warning" data-testid="filter-chip-sold">
            <span>{t('myListings.filterSold')} · {counts.sold}</span>
            <button onClick={clearSoldFilter} className="ml-1 p-0.5 rounded-full hover:bg-warning-soft transition-colors" aria-label={t('listing.clearFilters')} data-testid="clear-filter-btn">
              <XIcon size={12} weight="bold" />
            </button>
          </div>
        )}

        <MyListingsToolbar
          statusTab={statusTab}
          onStatusTabChange={(tab) => { setStatusTab(tab); if (soldOnly) clearSoldFilter(); }}
          counts={counts}
          category={category}
          onCategoryChange={setCategory}
          categoryOptions={categoryOptions}
          sortBy={sortBy}
          onSortChange={setSortBy}
          search={search}
          onSearchChange={setSearch}
        />

        {statusTab === 'deleted' && !loading && (
          <div className="mb-4 text-xs text-text_muted bg-surface_muted border border-border rounded-lg px-3 py-2" data-testid="deleted-tab-note">
            {t('myListings.deletedTabNote')}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-text_muted">{t('common.loading')}</p>
          </div>
        ) : visibleListings.length === 0 ? (
          <div className="text-center py-12 bg-white border border-border rounded-md" data-testid="no-listings">
            <p className="text-text_muted text-lg mb-4">
              {statusTab !== 'all' || search || category !== 'all' || soldOnly ? t('myListings.noListingsInTab') : t('myListings.noListingsYet')}
            </p>
            <Link to="/create">
              <Button className="bg-primary hover:bg-primary-hover text-white">
                {t('nav.postAd')}
              </Button>
            </Link>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="my-listings-grid">
            {visibleListings.map((listing) => (
              <ListingCardGrid
                key={listing.listing_id}
                listing={listing}
                price={formatDualCurrency(listing.price_mga, secondaryCurrency).mga}
                stats={listingStats[listing.listing_id] || {}}
                {...cardProps}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="my-listings-list">
            {visibleListings.map((listing) => (
              <ListingCardList
                key={listing.listing_id}
                listing={listing}
                price={formatDualCurrency(listing.price_mga, secondaryCurrency).mga}
                stats={listingStats[listing.listing_id] || {}}
                {...cardProps}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation — soft-delete with 30-day restore window */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('listing.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('listing.deleteConfirmDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11" data-testid="delete-cancel-btn">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="min-h-11" variant="danger" onClick={handleDeleteConfirm} data-testid="delete-confirm-btn">{t('listing.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Promotion Modal */}
      <Dialog open={!!promoListing} onOpenChange={() => setPromoListing(null)}>
        <DialogContent className="max-w-md" data-testid="promote-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{t('promote.title')}</DialogTitle>
          </DialogHeader>
          {promoListing && (
            <div>
              <p className="text-sm text-text_muted mb-4">
                {promoListing.title}
              </p>
              <div className="space-y-3">
                {PROMO_TIERS.map(({ key, icon: Icon, color }) => {
                  const existing = getPromoStatus(promoListing.listing_id, key);
                  return (
                    <button
                      key={key}
                      onClick={() => !existing && handlePromote(key)}
                      disabled={promoting || !!existing}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${existing ? 'border-border bg-surface_muted opacity-60' : 'border-border hover:border-primary hover:shadow-sm'}`}
                      data-testid={`promo-tier-${key}`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={20} className="text-white" weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-text_main">{t(`promote.${key}`)}</p>
                        <p className="text-xs text-text_muted">{t(`promote.${key}Desc`)}</p>
                      </div>
                      {existing ? (
                        <Badge variant={existing.status === 'active' ? 'default' : 'secondary'} className="text-[10px] flex-shrink-0">
                          {existing.status === 'active' ? t('promote.active') : t('promote.pending')}
                        </Badge>
                      ) : (
                        <span className="text-xs text-primary font-semibold flex-shrink-0">{t('promote.free')}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-text_muted mt-4 text-center">{t('promote.reviewNote')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};
